import type { ProductTestDocumentFormValues } from "../schemas"
import type { ProductTestFormPayload, ProductTestOverrideResultsPayload } from "../types"

import { useCreateProductTest } from "./useCreateProductTest"
import { useUpdateProductTest } from "./useUpdateProductTest"

import type {
  CreateFileRecordInput,
  FieldGroupEnum,
  Product,
  ProductTestCreateInput,
  ProductTestUpdateInput,
  SignedUrl,
} from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { useFileUpload } from "@/hooks/useFileUpload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseProductTestFormSubmitProps {
  product: Product
  productTestDocuments: SignedUrl[]
  onSubmit: () => void
}

interface UseProductTestFormSubmitReturn {
  handleCreate: (payload: ProductTestFormPayload) => Promise<void>
  handleUpdate: (
    payload: ProductTestFormPayload | ProductTestOverrideResultsPayload,
    productTestId: string,
    isLotUpdated: boolean
  ) => Promise<void>
}

export const useProductTestFormSubmit = ({
  product,
  productTestDocuments,
  onSubmit,
}: UseProductTestFormSubmitProps): UseProductTestFormSubmitReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { create: createProductTest } = useCreateProductTest()
  const { update: updateProductTest } = useUpdateProductTest()
  const { uploadFile, createRecord: createFileRecord } = useFileUpload()
  const { removeSingleFile } = useFilesRemove()

  const handleDocumentsCreate = async (productTestId: string, documents: ProductTestDocumentFormValues[]) => {
    for (const document of documents) {
      await uploadFile({
        file: document.file!,
        type: document.type,
        label: document.description ?? "",
        metadata: {
          fieldGroup: document.type as FieldGroupEnum,
          referenceId: productTestId,
          referenceTableName: ReferenceTableName.ProductTestDocument,
        },
      })
    }
  }

  const handleDocumentsCopy = async (productTestId: string, documents: SignedUrl[]) => {
    for (const document of documents) {
      const { __typename, createdOn, isArchived, ...fileRecordValues } = document.file!
      await createFileRecord({
        ...fileRecordValues,
        fileId: crypto.randomUUID(),
        referenceId: productTestId,
      } as CreateFileRecordInput)
    }
  }

  const handleDocumentsDelete = async (fileIds: string[]) => {
    for (const fileId of fileIds) {
      await removeSingleFile(fileId)
    }
  }

  const handleCreate = async (payload: ProductTestFormPayload) => {
    const createdProductTest = await createProductTest({
      ...payload.productTest,
      productId: product.productId,
    } as ProductTestCreateInput)

    if (payload.documentsToCreate.length > 0) {
      await handleDocumentsCreate(createdProductTest.productTestId, payload.documentsToCreate)
    }

    notify({
      type: NotificationType.Success,
      message: "Product test data successfully created.",
    })
    onSubmit()
  }

  const handleUpdate = async (
    payload: ProductTestFormPayload | ProductTestOverrideResultsPayload,
    productTestId: string,
    isLotUpdated = false
  ) => {
    const { lotNumber, batchNumber, ...testValues } = payload.productTest
    const updatedProductTest = await updateProductTest({
      ...testValues,
      results: payload.productTest.results?.map((result) => {
        const { associatedTestTemplateId, ...resultValues } = result
        return {
          ...resultValues,
          productTestResultId: result.productTestResultId!,
        }
      }),
      productTestId,
      isLotUpdated,
    } as ProductTestUpdateInput)

    if (isLotUpdated) {
      await handleDocumentsCopy(updatedProductTest.productTestId, productTestDocuments)
    } else {
      await handleDocumentsDelete(payload.documentsToDelete)
    }

    if (payload.documentsToCreate.length > 0) {
      await handleDocumentsCreate(updatedProductTest.productTestId, payload.documentsToCreate)
    }

    notify({
      type: NotificationType.Success,
      message: "Product test data successfully updated.",
    })
    onSubmit()
  }

  return {
    handleCreate,
    handleUpdate,
  }
}
